<template>
  <div class="filters-portal">
    <component
      :is="componentMap.get(item.filter.key)"
      v-for="item in filterPayloads"
      :key="item.filter.key"
      v-bind="item"
      @filter-changed="$emit('filterChanged', $event)"
    />
  </div>
</template>
<script lang="ts" setup>
import type {
  AsyncComponentLoader,
  Component as ComponentInterface,
} from "vue";
import {
  FILTER_COMPONENTS_BY_KEYS,
  FILTER_COMPONENTS_BY_TYPE,
} from "~/modules/catalog/components/Filter/variables";
import { FiltersMode } from "~/modules/catalog/components/Filter/types";
import type { IFilterManager } from "~/modules/catalog/classes/IFilterManager";
import type { CategoryFull } from "~/types/category";

const props = defineProps<{
  filterWrapper?: ComponentInterface;
  filterManager?: IFilterManager;
  mode?: FiltersMode;
  category?: CategoryFull | null;
}>();

defineEmits<{
  (_event: "filterChanged", _param: any): void;
}>();

// Stable component resolution — only re-evaluates when filter keys/types change.
// Caches defineAsyncComponent results so Vue keeps the same component reference
// across re-renders, preventing unmount/remount blinks on filter state changes.
const componentCache = new Map<string, ComponentInterface>();

const componentMap = computed<Map<string, ComponentInterface>>(() => {
  const map = new Map<string, ComponentInterface>();
  const filters = props.filterManager?.filters || [];

  for (const rawFilter of filters) {
    const cached = componentCache.get(rawFilter.key);
    if (cached) {
      map.set(rawFilter.key, cached);
      continue;
    }

    const componentLoader: AsyncComponentLoader | undefined =
      FILTER_COMPONENTS_BY_KEYS[rawFilter.key] ||
      FILTER_COMPONENTS_BY_TYPE[rawFilter.type] ||
      undefined;

    if (componentLoader) {
      const component = defineAsyncComponent(componentLoader);
      componentCache.set(rawFilter.key, component);
      map.set(rawFilter.key, component);
    }
  }

  return map;
});

// Reactive payloads — re-evaluates when filter state changes (selections, counts, etc.)
const filterPayloads = computed(() => {
  const filters = props.filterManager?.filters || [];

  return filters
    .filter((f) => componentMap.value.has(f.key))
    .map((rawFilter) => ({
      filter: rawFilter as any,
      mode: props.mode,
      wrapper: props.filterWrapper,
      filterManager: props.filterManager,
      category: props.category,
    }));
});
</script>
